import { FileItem } from '@/components/smartai/api/detectRepository';
import type {
  AstAnalysis,
  ProcessFilesSummary,
} from '@/components/smartai/api/processFiles';
import type { ScmProviderId } from '@/types/scm-provider';
import type { PipelinePlatform } from '@/types/pipeline-platform';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ScanResult {
  astAnalysis: AstAnalysis;
  summary: ProcessFilesSummary;
}

interface OnboardingState {
  scmProvider: ScmProviderId | null;
  scanResult: ScanResult | null;
  prEmails: string[];
  autoPrEmails: string[];
  scanId: string;
  files: FileItem[];
  patToken: string;
  repoUrl: string;
  branchName: string;
  pipelineName: string;
  azureProject: string;
  repoId: string;
  repoName: string;
  branchId: string;
  org: string;
  testPatToken: string;
  testOrg: string;
  testProject: string;
  testPipelineName: string;
  testRepoUrl: string;
  testBranchName: string;
  testPipelinePlatform: PipelinePlatform | '';
  jenkinsBaseUrl: string;
  jenkinsUsername: string;
  jenkinsJobName: string;
  isFrontend: boolean;
  scanOnly: boolean;
}

const initialState: OnboardingState = {
  scmProvider: null,
  scanResult: null,
  prEmails: [],
  autoPrEmails: [],
  scanId: '',
  files: [],
  patToken: '',
  repoUrl: '',
  branchName: '',
  pipelineName: '',
  azureProject: '',
  repoId: '',
  repoName: '',
  branchId: '',
  org: '',
  testPatToken: '',
  testOrg: '',
  testProject: '',
  testPipelineName: '',
  testRepoUrl: '',
  testBranchName: '',
  testPipelinePlatform: '',
  jenkinsBaseUrl: '',
  jenkinsUsername: '',
  jenkinsJobName: '',
  isFrontend: false,
  scanOnly: false,
};

interface BatchInputs {
  scmProvider: ScmProviderId;
  files: FileItem[];
  patToken: string;
  repoUrl: string;
  branchName: string;
  pipelineName: string;
  azureProject: string;
  repoId: string;
  repoName: string;
  branchId: string;
  org: string;
  testPatToken: string;
  testOrg: string;
  testProject: string;
  testPipelineName: string;
  testRepoUrl: string;
  testBranchName: string;
  testPipelinePlatform?: PipelinePlatform | '';
  jenkinsBaseUrl?: string;
  jenkinsUsername?: string;
  jenkinsJobName?: string;
  isFrontend: boolean;
}

export const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setScmProvider: (state, action: PayloadAction<ScmProviderId>) => {
      state.scmProvider = action.payload;
    },
    setOnboardingEmails: (
      state,
      action: PayloadAction<{ prEmails: string[]; autoPrEmails: string[] }>,
    ) => {
      state.prEmails = action.payload.prEmails;
      state.autoPrEmails = action.payload.autoPrEmails;
    },
    setScanId: (state, action: PayloadAction<string>) => {
      state.scanId = action.payload;
    },
    setScanResult: (state, action: PayloadAction<ScanResult>) => {
      state.scanResult = action.payload;
    },
    setScanOnly: (state, action: PayloadAction<boolean>) => {
      state.scanOnly = action.payload;
    },
    setOnboardingBatchInputs: (state, action: PayloadAction<BatchInputs>) => {
      state.scmProvider = action.payload.scmProvider;
      state.files = action.payload.files;
      state.patToken = action.payload.patToken;
      state.repoUrl = action.payload.repoUrl;
      state.branchName = action.payload.branchName;
      state.pipelineName = action.payload.pipelineName;
      state.azureProject = action.payload.azureProject;
      state.repoId = action.payload.repoId;
      state.repoName = action.payload.repoName;
      state.branchId = action.payload.branchId;
      state.org = action.payload.org;
      state.testPatToken = action.payload.testPatToken;
      state.testOrg = action.payload.testOrg;
      state.testProject = action.payload.testProject;
      state.testPipelineName = action.payload.testPipelineName;
      state.testRepoUrl = action.payload.testRepoUrl;
      state.testBranchName = action.payload.testBranchName;
      state.testPipelinePlatform = action.payload.testPipelinePlatform ?? '';
      state.jenkinsBaseUrl = action.payload.jenkinsBaseUrl ?? '';
      state.jenkinsUsername = action.payload.jenkinsUsername ?? '';
      state.jenkinsJobName = action.payload.jenkinsJobName ?? '';
      state.isFrontend = action.payload.isFrontend;
    },
  },
});

export const {
  setScmProvider,
  setOnboardingEmails,
  setScanId,
  setScanResult,
  setScanOnly,
  setOnboardingBatchInputs,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
